import { Tooltip } from '../../../stores/tooltips.store';
import tooltipStyles from '../tooltip/tooltip.module.scss';
import { useHover } from '@monorepo/tools/src/lib/hooks/utils/use-hover';
import { useDebounce } from '@monorepo/tools/src/lib/hooks/utils/use-debounce';
import { Alignments, TooltipThemes } from '../tooltip/tooltip';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface IIndependentTooltip {
	tooltip: Tooltip;
}

const themeStyles = {
	[TooltipThemes.Dark]: tooltipStyles.dark,
	[TooltipThemes.Light]: tooltipStyles.light,
};

const VIEWPORT_MARGIN_PX = 8;
/** Above typical modal layers so portaled tooltips are not clipped by stacking context. */
const TOOLTIP_PORTAL_Z_INDEX = 10000;

function alignToClassName(align?: Alignments): string {
	switch (align) {
		case Alignments.FromRight:
			return tooltipStyles.alignToRight;
		case Alignments.FromLeft:
			return tooltipStyles.alignToLeft;
		case Alignments.FromBottom:
			return tooltipStyles.alignToBottom;
		default:
			return '';
	}
}

export const IndependentTooltip = (props: IIndependentTooltip) => {
	const { tooltip } = props;
	const { hoverRef, delay, theme, content, is, direction, forceHover, isHoveredOnTooltip, maxHeight, align } = tooltip;
	const tooltipRef = useRef<HTMLDivElement>(null);
	const bubbleRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState({ top: 0, left: 0 });
	const [directionClass] = useState<string>(direction ? tooltipStyles[direction] : '');
	const alignClassName = alignToClassName(align);

	const isHover = useHover(hoverRef);
	const isTooltipHovered = useHover(tooltipRef);

	const debouncedIsHover = useDebounce((isHoveredOnTooltip && isTooltipHovered) || forceHover || isHover, delay);

	const syncPosition = useCallback(() => {
		const trigger = hoverRef.current;
		if (!trigger) {
			return;
		}
		const rect = trigger.getBoundingClientRect();
		const anchorCenterX = rect.left + rect.width / 2;
		const anchorTop = rect.top;

		setPosition({ top: anchorTop, left: anchorCenterX });

		const shouldClamp = debouncedIsHover || forceHover;
		if (!shouldClamp) {
			return;
		}

		requestAnimationFrame(() => {
			const bubble = bubbleRef.current;
			if (!bubble) {
				return;
			}
			const br = bubble.getBoundingClientRect();
			let dx = 0;
			if (br.right > window.innerWidth - VIEWPORT_MARGIN_PX) {
				dx += window.innerWidth - VIEWPORT_MARGIN_PX - br.right;
			}
			if (br.left + dx < VIEWPORT_MARGIN_PX) {
				dx = VIEWPORT_MARGIN_PX - br.left;
			}
			if (dx !== 0) {
				setPosition((p) => ({ ...p, left: p.left + dx }));
			}
		});
	}, [debouncedIsHover, forceHover]);

	useLayoutEffect(() => {
		if (!isHover && !debouncedIsHover && !forceHover) {
			return;
		}
		syncPosition();
	}, [isHover, debouncedIsHover, forceHover, syncPosition]);

	useLayoutEffect(() => {
		if (!debouncedIsHover && !isHover && !forceHover) {
			return;
		}
		const onScrollOrResize = () => syncPosition();
		window.addEventListener('scroll', onScrollOrResize, true);
		window.addEventListener('resize', onScrollOrResize);
		return () => {
			window.removeEventListener('scroll', onScrollOrResize, true);
			window.removeEventListener('resize', onScrollOrResize);
		};
	}, [debouncedIsHover, isHover, forceHover, syncPosition]);

	if (!is) {
		return null;
	}

	const tooltipTree = (
		<div
			style={{
				top: position.top,
				left: position.left,
				position: 'fixed',
				width: 0,
				height: 0,
				zIndex: TOOLTIP_PORTAL_Z_INDEX,
				pointerEvents: debouncedIsHover ? 'auto' : 'none',
			}}
			ref={tooltipRef}>
			<span className={`${tooltipStyles.wrapper}`}>
				<div
					ref={bubbleRef}
					style={{ maxHeight: maxHeight ? `${maxHeight}px` : undefined, overflow: maxHeight ? 'auto' : undefined }}
					className={`${tooltipStyles.tooltip} ${debouncedIsHover ? tooltipStyles.active : ''} ${
						themeStyles[theme]
					} ${directionClass} ${alignClassName}`}>
					{content}
				</div>
			</span>
		</div>
	);

	if (typeof document === 'undefined' || !document.body) {
		return null;
	}

	return createPortal(tooltipTree, document.body);
};
